import '@material/web/icon/icon.js'
import '@material/web/fab/fab.js'
import '@operato/dataset/ox-data-sample-view.js'
import '@operato/dataset/ox-data-ooc-brief-view.js'
import '@operato/dataset/ox-checklist-sample-view.js'
import '@things-factory/worklist/dist-client/components/activity-thread-timeline.js'

import gql from 'graphql-tag'
import { css, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { ScrollbarStyles } from '@operato/styles'
import { DataSample } from '@operato/dataset'
import { GhostPrint } from '@operato/ghost-print'

@customElement('data-sample-view')
export class DataSampleView extends localize(i18next)(LitElement) {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        position: relative;
        display: block;
        overflow: auto;
        background-color: var(--md-sys-color-surface);
      }

      ox-data-sample-view,
      ox-checklist-sample-view,
      ox-data-ooc-brief-view {
        padding: var(--spacing-large);
      }

      activity-thread-timeline {
        margin: var(--spacing-medium);
      }

      md-fab {
        position: fixed;
        bottom: 15px;
        right: 16px;
        text-decoration: auto;
        opacity: 0.5;
      }

      @media print {
        md-fab {
          display: none;
        }
      }
    `
  ]

  @property({
    type: String,
    attribute: 'data-sample-id'
  })
  dataSampleId?: string

  @state() dataSample?: DataSample

  private printHandler = this.handlePrint.bind(this)

  render() {
    const dataSample = this.dataSample

    if (!dataSample) {
      return nothing
    }

    if (dataSample.dataOoc) {
      var reviewActivityThread = dataSample.dataOoc.reviewActivityInstance?.activityThreads?.[0]
      var resolveActivityThread = dataSample.dataOoc.resolveActivityInstance?.activityThreads?.[0]
    } else {
      var reviewActivityThread = dataSample?.reviewActivityInstance?.activityThreads?.[0]
    }

    return html`
      ${!dataSample?.dataOoc
        ? html`${this.renderDataSample()}`
        : html` <ox-data-ooc-brief-view .dataOoc=${dataSample!.dataOoc}></ox-data-ooc-brief-view>`}
      ${reviewActivityThread
        ? html`
            <activity-thread-timeline
              subtitle=${String(
                i18next.t(dataSample?.dataOoc ? 'label.ooc review timeline' : 'label.datasample review timeline')
              )}
              .activityThread=${reviewActivityThread}
              short-form
            ></activity-thread-timeline>
          `
        : nothing}
      ${resolveActivityThread
        ? html`
            <activity-thread-timeline
              subtitle=${String(i18next.t('label.ooc resolve timeline'))}
              .activityThread=${resolveActivityThread}
              short-form
            ></activity-thread-timeline>
          `
        : nothing}

      <md-fab
        title="print"
        @click=${() =>
          GhostPrint.print({
            src: `./data-sample/${this.dataSampleId}`
          })}
      >
        <md-icon slot="icon">print</md-icon>
      </md-fab>
    `
  }

  updated(changes) {
    if (changes.has('dataSampleId')) {
      this.fetchDataSample()
    }
  }

  private renderDataSample() {
    const dataSet = this.dataSample?.dataSet
    const monitorType = dataSet?.monitorType

    switch (monitorType) {
      case 'generated':
        return html`<ox-data-sample-view .dataSample=${this.dataSample}></ox-data-sample-view>`
      case 'checklist':
        return html`<ox-checklist-sample-view .dataSample=${this.dataSample}></ox-checklist-sample-view>`
      case 'board':
        break
      case 'page':
        break
      default:
        return html`<ox-data-sample-view .dataSample=${this.dataSample}></ox-data-sample-view>`
        break
    }
  }

  async fetchDataSample() {
    const id = this.dataSampleId

    const response = await client.query({
      query: gql`
        query ($id: String!) {
          dataSample(id: $id) {
            id
            name
            description
            useCase
            key01
            key02
            key03
            key04
            key05
            data
            ooc
            oos
            updatedAt
            collectedAt
            updater {
              id
              name
            }
            dataSet {
              id
              name
              description
              monitorType
              monitorView
            }
            dataItems {
              name
              description
              active
              hidden
              tag
              group
              subgroup
              type
              unit
              options
              quota
              spec
              stat
            }
            judgment
            workDate
            workShift
            reviewActivityInstance {
              ...ActivityInstanceFragment
            }
            dataOoc {
              name
              description
              useCase
              dataSet {
                id
                name
                monitorType
                monitorView
              }
              key01
              key02
              key03
              key04
              key05
              partitionKeys
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                subgroup
                type
                unit
                options
                quota
                spec
              }
              data
              rawData
              judgment
              ooc
              oos
              state
              history
              workDate
              workShift
              correctiveInstruction
              correctiveAction
              reviewedAt
              reviewer {
                id
                name
              }
              correctedAt
              corrector {
                id
                name
              }
              updater {
                id
                name
              }
              updatedAt
              collectedAt

              reviewActivityInstance {
                ...ActivityInstanceFragment
              }
              resolveActivityInstance {
                ...ActivityInstanceFragment
              }
            }
          }
        }

        fragment ActivityInstanceFragment on ActivityInstance {
          activityThreads {
            assignee {
              name
            }
            output
            activityApprovals {
              judgment
              approver {
                name
              }
              comment
              createdAt
              updatedAt
              terminatedAt
            }
            activityThreadHistories {
              transaction
              reason
              updater {
                name
              }
              updatedAt
              state
              output
            }
          }
        }
      `,
      variables: {
        id
      }
    })

    this.dataSample = response.data.dataSample
  }

  handlePrint(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
      event.preventDefault()

      GhostPrint.print({
        src: `./data-sample/${this.dataSampleId}`
      })
    }
  }

  connectedCallback(): void {
    super.connectedCallback()
    window.addEventListener('keydown', this.printHandler)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    window.removeEventListener('keydown', this.printHandler)
  }
}
